import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, Save, TrendingUp, Settings } from "lucide-react";
import { getElectionResults, saveElectionResults, NationalElectionResult, ElectionPartyResult } from "@/data/electionResultsStore";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";

export function AdminElectionResultsTab() {
  const { toast } = useToast();
  const [data, setData] = useState<NationalElectionResult | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(getElectionResults());
    
    const handleUpdate = () => {
      setData(getElectionResults());
    };
    window.addEventListener("electionResultsUpdated", handleUpdate);
    return () => window.removeEventListener("electionResultsUpdated", handleUpdate);
  }, []);

  if (!data) return <div>Chargement...</div>;

  const handleConfigChange = (field: keyof typeof data.config, value: string | boolean) => {
    setData({
      ...data,
      config: {
        ...data.config,
        [field]: value,
      },
    });
  };

  const handlePartyResultChange = (index: number, field: keyof ElectionPartyResult, value: string) => {
    const newResults = [...data.results];
    const numValue = field !== "partyAcronym" ? Number(value) : value;
    
    newResults[index] = {
      ...newResults[index],
      [field]: numValue,
    } as any;
    
    setData({
      ...data,
      results: newResults,
    });
  };

  const handleSave = () => {
    setIsSaving(true);
    try {
      saveElectionResults({
        ...data,
        lastUpdate: new Date().toISOString()
      });
      toast({
        title: "Résultats enregistrés",
        description: "Le baromètre des élections a été mis à jour avec succès."
      });
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer les résultats.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const declaredSeats = data.results.reduce((acc, p) => acc + (p.seats || 0), 0);
  const progressPct = Math.round((declaredSeats / data.totalSeats) * 100) || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-display font-bold">Gestion des Résultats (Nuit Électorale)</h2>
          <p className="text-sm text-muted-foreground">Mettez à jour manuellement ou configurez l'automatisation.</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
          {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer les modifications
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Configuration générale */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-base font-display flex items-center gap-2">
              <Settings className="w-4 h-4 text-primary" /> Configuration de l'affichage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                <div>
                  <Label htmlFor="autoUpdate" className="font-semibold cursor-pointer">Actualisation automatique</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Lier à une source externe (API)</p>
                </div>
                <Switch 
                  id="autoUpdate" 
                  checked={data.config.autoUpdate} 
                  onCheckedChange={(checked) => handleConfigChange("autoUpdate", checked)} 
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5">
                <div>
                  <Label htmlFor="isResultsNightActive" className="font-semibold cursor-pointer text-primary">Nuit des résultats</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Mettre les résultats en ligne</p>
                </div>
                <Switch 
                  id="isResultsNightActive" 
                  checked={data.config.isResultsNightActive || false} 
                  onCheckedChange={(checked) => handleConfigChange("isResultsNightActive", checked)} 
                />
              </div>
            </div>
            
            {data.config.autoUpdate && (
              <div className="space-y-1.5">
                <Label className="text-xs">URL source de données externe (API)</Label>
                <input 
                  type="text" 
                  value={data.config.externalUrl} 
                  onChange={(e) => handleConfigChange("externalUrl", e.target.value)}
                  placeholder="https://api.interieur.gov.ma/results2026..."
                  className="w-full text-sm h-9 rounded-md border border-input bg-background px-3 focus:ring-1 focus:ring-primary/50"
                />
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase text-muted-foreground">Texte Français</h4>
                <div className="space-y-1.5">
                  <Label className="text-[10px]">Titre Principal</Label>
                  <input type="text" value={data.config.titleFr} onChange={(e) => handleConfigChange("titleFr", e.target.value)} className="w-full text-xs h-8 rounded-md border border-input bg-background px-2" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px]">Sous-titre explicatif</Label>
                  <textarea value={data.config.subtitleFr} onChange={(e) => handleConfigChange("subtitleFr", e.target.value)} rows={2} className="w-full text-xs rounded-md border border-input bg-background px-2 py-1" />
                </div>
              </div>
              <div className="space-y-3" dir="rtl">
                <h4 className="text-xs font-bold uppercase text-muted-foreground">Texte Arabe</h4>
                <div className="space-y-1.5">
                  <Label className="text-[10px]">العنوان الرئيسي</Label>
                  <input type="text" value={data.config.titleAr} onChange={(e) => handleConfigChange("titleAr", e.target.value)} className="w-full text-xs h-8 rounded-md border border-input bg-background px-2 font-display" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px]">وصف فرعي</Label>
                  <textarea value={data.config.subtitleAr} onChange={(e) => handleConfigChange("subtitleAr", e.target.value)} rows={2} className="w-full text-xs rounded-md border border-input bg-background px-2 py-1 font-display" />
                </div>
              </div>
            </div>
            
            <div className="space-y-1.5 pt-2">
              <Label className="text-xs font-semibold">Total sièges (Chambre)</Label>
              <input 
                type="number" 
                value={data.totalSeats} 
                onChange={(e) => setData({...data, totalSeats: Number(e.target.value)})}
                className="w-full sm:w-1/3 text-sm h-9 rounded-md border border-input bg-background px-3"
              />
            </div>
          </CardContent>
        </Card>

        {/* Saisie des Résultats */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
            <CardTitle className="text-base font-display flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Saisie des Sièges
            </CardTitle>
            <div className="text-xs font-bold px-2 py-1 bg-muted rounded-full">
              Dépouillé : <span className={progressPct > 90 ? "text-emerald-600" : "text-amber-600"}>{progressPct}%</span> ({declaredSeats}/{data.totalSeats})
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0 uppercase font-semibold text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Parti</th>
                    <th className="px-4 py-2 text-right">Sièges</th>
                    <th className="px-4 py-2 text-right">Voix</th>
                    <th className="px-4 py-2 text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((result, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-4 py-2 font-bold">
                        <input 
                          type="text" 
                          list="all-parties-list"
                          value={result.partyAcronym} 
                          onChange={(e) => handlePartyResultChange(idx, "partyAcronym", e.target.value)}
                          disabled={data.config.autoUpdate}
                          placeholder="Nom du parti"
                          className="w-full min-w-[120px] font-bold h-7 rounded border border-input px-2 bg-background focus:ring-1 focus:ring-primary/50 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input 
                          type="number" 
                          value={result.seats} 
                          onChange={(e) => handlePartyResultChange(idx, "seats", e.target.value)}
                          disabled={data.config.autoUpdate}
                          className="w-full min-w-[60px] text-right font-mono font-bold h-7 rounded border border-input px-2 focus:border-primary disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input 
                          type="number" 
                          value={result.votes} 
                          onChange={(e) => handlePartyResultChange(idx, "votes", e.target.value)}
                          disabled={data.config.autoUpdate}
                          className="w-full min-w-[80px] text-right font-mono h-7 rounded border border-input px-2 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input 
                          type="number" 
                          step="0.1"
                          value={result.percentage} 
                          onChange={(e) => handlePartyResultChange(idx, "percentage", e.target.value)}
                          disabled={data.config.autoUpdate}
                          className="w-full min-w-[60px] text-right font-mono h-7 rounded border border-input px-2 disabled:opacity-50"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {!data.config.autoUpdate && (
                <div className="p-4 border-t flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setData({
                        ...data,
                        results: [...data.results, { partyAcronym: "Nouveau", seats: 0, votes: 0, percentage: 0 }]
                      });
                    }}
                  >
                    + Ajouter un parti
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      const existing = new Set(data.results.map(r => r.partyAcronym));
                      const toAdd = allPartiesIncludingNonParliamentary
                        .filter(p => !existing.has(p.acronym))
                        .map(p => ({ partyAcronym: p.acronym, seats: 0, votes: 0, percentage: 0 }));
                      if (toAdd.length) {
                        setData({ ...data, results: [...data.results, ...toAdd] });
                      }
                    }}
                  >
                    + Ajouter tous les partis restants
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <datalist id="all-parties-list">
        {allPartiesIncludingNonParliamentary.map(p => (
          <option key={p.acronym} value={p.acronym}>{p.acronym}{p.acronymAr ? ` - ${p.acronymAr}` : ''}</option>
        ))}
      </datalist>
    </div>
  );
}
